import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { WorkflowJob } from '../../graphql/types';
import type { TitleAnalysisReport } from '../../titleAnalysis';
import { adaptTitleAnalysisReport } from '../../components/titleAnalysis/reportAdapter';
import ExceptionsTab from './ExceptionsTab';
import { GradeCard, SummaryCard } from './ReportSummary';
import { CommitmentTab, EncumbrancesTab, PartiesTab, VestingChainTab } from './ReportTabPanels';
import { StatusCards } from './StatusCards';
import {
  DISCLAIMER_TEXT,
  REPORT_TAB_LABELS,
  tabCount,
  type ReportTabId,
} from './reportModel';
import { ReportCard } from './reportParts';

/**
 * TitleAnalysisReportBody: the redesigned Title analysis report.
 *
 * Grade card and summary/rationale, three status cards, tabs (Exceptions,
 * Vesting chain, Encumbrances, Parties, Commitment) and the disclaimer. The
 * stored report is adapted with `adaptTitleAnalysisReport`. The Exceptions tab
 * is the separate `ExceptionsTab` component (severity filter and expandable
 * rows). Light theme only.
 *
 * Requirements: 11.6, 11.7, 11.8, 11.9, 11.10.
 */
export interface TitleAnalysisReportBodyProps {
  report: TitleAnalysisReport;
  job: WorkflowJob;
}

export const TitleAnalysisReportBody: React.FC<TitleAnalysisReportBodyProps> = ({
  report,
  job,
}) => {
  const adapted = useMemo(() => adaptTitleAnalysisReport(report, job), [report, job]);
  const [tab, setTab] = useState<ReportTabId>('exceptions');
  const tabRefs = useRef<Partial<Record<ReportTabId, HTMLButtonElement | null>>>({});

  const onTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const last = REPORT_TAB_LABELS.length - 1;
      let next = index;
      if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;
      event.preventDefault();
      const id = REPORT_TAB_LABELS[next].id;
      setTab(id);
      tabRefs.current[id]?.focus();
    },
    []
  );

  return (
    <div className="flex flex-col gap-4" data-testid="title-analysis-report">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <GradeCard grade={adapted.grade} />
        <SummaryCard summary={adapted.summary} rationale={adapted.gradeRationale} />
      </div>

      <StatusCards report={adapted} />

      <ReportCard testId="title-analysis-tabs" className="!p-0">
        <div
          role="tablist"
          aria-label="Title analysis report sections"
          className="flex flex-wrap gap-1 border-b border-gray-200 px-3 pt-2"
        >
          {REPORT_TAB_LABELS.map(({ id, label }, index) => {
            const selected = tab === id;
            const count = tabCount(id, adapted);
            return (
              <button
                key={id}
                ref={(el) => {
                  tabRefs.current[id] = el;
                }}
                type="button"
                role="tab"
                id={`title-analysis-tab-${id}`}
                aria-selected={selected}
                aria-controls={`title-analysis-panel-${id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setTab(id)}
                onKeyDown={(e) => onTabKeyDown(e, index)}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] ${
                  selected
                    ? 'border-[#834DFB] text-[#5A21C9]'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
                {count !== null ? (
                  <span
                    className={`rounded-md px-1.5 text-xs ${
                      selected ? 'bg-[#EDE7FF] text-[#5A21C9]' : 'bg-[#f3f4f6] text-[#4b5563]'
                    }`}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div
          role="tabpanel"
          id={`title-analysis-panel-${tab}`}
          aria-labelledby={`title-analysis-tab-${tab}`}
          tabIndex={0}
          className="p-[18px] focus:outline-none"
        >
          {tab === 'exceptions' ? <ExceptionsTab exceptions={adapted.exceptions} /> : null}
          {tab === 'vesting' ? <VestingChainTab vestingChain={adapted.vestingChain} /> : null}
          {tab === 'encumbrances' ? (
            <EncumbrancesTab encumbrances={adapted.encumbrances} />
          ) : null}
          {tab === 'parties' ? <PartiesTab entities={adapted.entities} /> : null}
          {tab === 'commitment' ? <CommitmentTab commitment={adapted.commitment} /> : null}
        </div>
      </ReportCard>

      <aside
        data-testid="title-analysis-disclaimer"
        aria-label="Disclaimer"
        className="flex items-start gap-3 rounded-xl border border-[#fef08a] bg-[#fefce8] p-4"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#854d0e]" aria-hidden="true" />
        <p className="text-[13px] text-[#854d0e]">
          <span className="font-bold">Disclaimer:</span> {DISCLAIMER_TEXT} Analysis date{' '}
          {adapted.analysisDate}.
        </p>
      </aside>
    </div>
  );
};

export default TitleAnalysisReportBody;
